import { useTranslation } from 'react-i18next';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';

import { Term } from '../i18n/Term';
import { authApi } from '../net/rest';
import { useStore } from '../store';
import { pendingBadgeCount } from '../store/social';
import { Badge } from '../ui/Badge';

import styles from './AppShell.module.css';
import { FriendsSheet } from './social/FriendsSheet';

/**
 * Below this a right-side drawer covers the page it is supposed to sit beside,
 * so the button routes to `/friends` instead of opening the sheet. Matches the
 * breakpoint in `FriendsPanel.module.css`.
 */
const SHEET_MIN_WIDTH = 480;

/** The frame around every signed-in route: wordmark, nav, and who you are. */
export function AppShell() {
  const { t } = useTranslation('common');
  const { t: ts } = useTranslation('social');
  const navigate = useNavigate();
  const user = useStore((s) => s.auth.user);
  const clear = useStore((s) => s.auth.clear);
  const incoming = useStore((s) => s.social.incoming);
  const invites = useStore((s) => s.social.invites);
  // In the store rather than in local state, so the lobby's "friends online"
  // strip can open the same sheet without this component having to hand a
  // setter down through two routes that do not otherwise know about each other.
  const sheetOpen = useStore((s) => s.social.panelOpen);
  const setSheetOpen = useStore((s) => s.social.setPanelOpen);
  const waiting = pendingBadgeCount(incoming, invites);

  async function signOut() {
    // The cookie is cleared server-side; failing that, dropping the in-memory
    // token still ends the session on this device.
    await authApi.logout().catch(() => undefined);
    clear();
    void navigate('/login', { replace: true });
  }

  return (
    <div className={styles.shell}>
      <header className={styles.header}>
        <Link to="/lobby" className={styles.brand} aria-label="Órdago">
          <Term k="brand" className={styles.brandTerm} />
        </Link>

        <nav className={styles.nav} aria-label={t('nav.menu')}>
          <NavLink
            to="/lobby"
            className={({ isActive }) => (isActive ? styles.linkActive : styles.link)}
          >
            {t('nav.lobby')}
          </NavLink>
          <NavLink
            to="/profile"
            className={({ isActive }) => (isActive ? styles.linkActive : styles.link)}
            data-testid="nav-profile"
          >
            {t('nav.profile')}
          </NavLink>
          {/* No admin link, deliberately. `/admin` has no route — the panel is
              v2 (decisions.md, "Admin v1 = minimal") and the v1 actions
              (ban/unban/delete/force-close) exist only as API endpoints. The
              link shipped ahead of the screen and sent every admin who clicked
              it to the 404 page. The `admin` badge below still marks the
              account, which is the part that was true. */}
        </nav>

        <div className={styles.identity}>
          <button
            type="button"
            className={styles.friends}
            data-testid="nav-friends"
            aria-label={ts('openPanel')}
            onClick={() => {
              if (window.innerWidth < SHEET_MIN_WIDTH) {
                void navigate('/friends');
                return;
              }
              setSheetOpen(true);
            }}
          >
            {ts('title')}
            {waiting > 0 ? (
              <span className={styles.friendsBadge} data-testid="friends-badge">
                {waiting}
              </span>
            ) : null}
          </button>

          {user === null ? null : (
            <>
              {user.role === 'admin' ? <Badge tone="gold">{t('nav.admin')}</Badge> : null}
              <img
                className={styles.avatar}
                src={user.avatar.url64}
                alt={t('avatar.of', { name: user.username })}
                width={32}
                height={32}
              />
              <span className={styles.username}>{user.username}</span>
            </>
          )}
          <button
            type="button"
            className={styles.signOut}
            onClick={() => void signOut()}
            data-testid="sign-out"
          >
            {t('nav.logout')}
          </button>
        </div>
      </header>

      <main className={styles.main}>
        <Outlet />
      </main>

      {/* No `onInvite`: the shell is never at a table, so the Invitar buttons
          are absent here and present only in the room's own copy. */}
      <FriendsSheet open={sheetOpen} onOpenChange={setSheetOpen} />
    </div>
  );
}
